import { useState, type FormEvent } from 'react';
import { useLocalization } from '@/hooks/useLocalization';
import {
  validatePasswords,
  CREATE_PASSWORD_MESSAGE,
  PASSWORD_TOOLTIP_SPECIAL_CHARACTERS,
} from '@/utils/formValidators';
import { useResetPassword } from './hooks/useResetPassword';
import { SupportWarningDialog } from './components/SupportWarningDialog';
import { AppHeader } from '@/components/layout/AppHeader';
import { AppFooter } from '@/components/layout/AppFooter';
import styles from './ResetPasswordPage.module.css';

/**
 * ResetPasswordPage — handles the Firebase password reset link.
 *
 * Mirrors Flutter ResetPassword + ResetPasswordConsumer:
 *   1. User opens the reset link from the email with ?oobCode=&tenantId=
 *   2. The form shows straight away while the code is validated (spinner over it)
 *   3. On submit (FormValidators.validatePasswords): confirmPasswordReset via Firebase
 *   4. Expired link, success or failure: toast → /home (see useResetPassword)
 *
 * Flutter source files:
 *   - presentation/web/auth/reset_password/reset_password.dart
 *   - application/web_auth/web_login/web_login_bloc.dart
 *   - domain/core/constants/string_constants.dart (WebResetPwd, WebSignUpPageConstants)
 */

export function ResetPasswordPage() {
  const { t } = useLocalization();

  const { isLoading, warningMessage, handleResetPassword, clearWarning } = useResetPassword();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [confirmError, setConfirmError] = useState('');

  function handleSubmit(e: FormEvent) {
    e.preventDefault();

    const pwErr = validatePasswords({ pass: password }, t);
    const cpErr = validatePasswords({ pass: password, confirmPass: confirmPassword, isConfirm: true }, t);
    setPasswordError(pwErr);
    setConfirmError(cpErr);

    if (pwErr || cpErr) return;

    handleResetPassword(password);
  }

  return (
    <div className={styles.pageContainer}>
      <AppHeader />

      <div className={styles.centerContent}>
        <div className={styles.formCard}>
          <form onSubmit={handleSubmit} noValidate>
            {/* New Password */}
            <div className={styles.fieldGroup}>
              <label className={styles.label} htmlFor="reset-password">
                {t('Reset Password')} <span className={styles.required}>*</span>
                <span
                  className={styles.infoTip}
                  title={t(CREATE_PASSWORD_MESSAGE) + PASSWORD_TOOLTIP_SPECIAL_CHARACTERS}
                >
                  ℹ
                </span>
              </label>
              <div className={styles.passwordWrapper}>
                <input
                  id="reset-password"
                  type={showPassword ? 'text' : 'password'}
                  className={`${styles.input} ${passwordError ? styles.inputError : ''}`}
                  placeholder={t('Enter your Password')}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (passwordError) setPasswordError('');
                  }}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className={styles.eyeBtn}
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? '👁' : '👁‍🗨'}
                </button>
              </div>
              {passwordError && <span className={styles.fieldError}>{passwordError}</span>}
            </div>

            {/* Confirm Password */}
            <div className={styles.fieldGroup}>
              <label className={styles.label} htmlFor="reset-password-confirm">
                {t('Re-type your password')} <span className={styles.required}>*</span>
              </label>
              <div className={styles.passwordWrapper}>
                <input
                  id="reset-password-confirm"
                  type={showConfirmPassword ? 'text' : 'password'}
                  className={`${styles.input} ${confirmError ? styles.inputError : ''}`}
                  placeholder={t('Re-type your password')}
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (confirmError) setConfirmError('');
                  }}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className={styles.eyeBtn}
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  tabIndex={-1}
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  {showConfirmPassword ? '👁' : '👁‍🗨'}
                </button>
              </div>
              {confirmError && <span className={styles.fieldError}>{confirmError}</span>}
            </div>

            <button type="submit" className={styles.submitBtn} disabled={isLoading}>
              {t('Continue')}
            </button>
          </form>
        </div>
      </div>

      {/* The footer is hidden while loading, as in Flutter */}
      {!isLoading && <AppFooter />}

      {/* Loading overlay — Flutter ModalProgressHUD */}
      {isLoading && (
        <div className={styles.loadingOverlay}>
          <div className={styles.spinner} />
        </div>
      )}

      {warningMessage && (
        <SupportWarningDialog message={warningMessage} onOk={clearWarning} />
      )}
    </div>
  );
}
